import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useEntityRegistry } from "../api/registry";
import type {
  SourceEntity,
  GroupEntity,
  CategoryEntity,
  ExpenseTypeEntity,
  PayeeEntity,
  InvoiceEntity,
} from "../api/types";

/**
 * The registry with hidden entities kept in.
 *
 * Hiding an entity means "don't offer it in pickers", not "forget its name":
 * anything that *displays* what a record references — an invoice's sender, a
 * transaction's source/group/category chip — resolves against this, so a
 * hidden payee's invoices keep its name and logo. Pickers, and anything that
 * proposes a value to pick, use the visible lists and maps on the context.
 */
export interface NamingRegistry {
  sources: SourceEntity[];
  groups: GroupEntity[];
  categories: CategoryEntity[];
  expenseTypes: ExpenseTypeEntity[];
  payees: PayeeEntity[];

  getSource: (name: string) => SourceEntity | undefined;
  getGroup: (name: string) => GroupEntity | undefined;
  getCategory: (name: string) => CategoryEntity | undefined;
  getExpenseType: (name: string) => ExpenseTypeEntity | undefined;
  getPayee: (name: string) => PayeeEntity | undefined;
}

interface RegistryContextType {
  /** Visible entities only — what pickers offer. */
  sources: SourceEntity[];
  groups: GroupEntity[];
  categories: CategoryEntity[];
  expenseTypes: ExpenseTypeEntity[];
  payees: PayeeEntity[];
  invoices: InvoiceEntity[];

  sourceMap: Record<string, SourceEntity | undefined>;
  groupMap: Record<string, GroupEntity | undefined>;
  categoryMap: Record<string, CategoryEntity | undefined>;
  expenseTypeMap: Record<string, ExpenseTypeEntity | undefined>;
  payeeMap: Record<string, PayeeEntity | undefined>;
  invoiceMap: Record<string, InvoiceEntity | undefined>;

  loading: boolean;
  error: Error | null;

  getSource: (name: string) => SourceEntity | undefined;
  getGroup: (name: string) => GroupEntity | undefined;
  getCategory: (name: string) => CategoryEntity | undefined;
  getExpenseType: (name: string) => ExpenseTypeEntity | undefined;
  getPayee: (name: string) => PayeeEntity | undefined;
  getInvoice: (id: string) => InvoiceEntity | undefined;

  withHidden: NamingRegistry;
}

type Named = { name: string; priority?: number; hidden?: boolean };
type NameMap<T> = Record<string, T | undefined>;

const byPriorityThenName = (a: Named, b: Named) =>
  (b.priority || 0) - (a.priority || 0) || a.name.localeCompare(b.name);

const sortedValues = <T extends Named>(map: NameMap<T>): T[] =>
  Object.values(map)
    .filter((x): x is T => x !== undefined)
    .sort(byPriorityThenName);

/** The same map without hidden entries — what the server's default
 * (visible-only) registry returns, without a second request for it. */
const visibleOnly = <T extends Named>(map: NameMap<T>): NameMap<T> =>
  Object.fromEntries(Object.entries(map).filter(([, e]) => e && !e.hidden));

const RegistryContext = createContext<RegistryContextType | undefined>(
  undefined,
);

export const RegistryProvider = ({ children }: { children: ReactNode }) => {
  // One request with hidden entities included; the visible maps are filtered
  // from it. The naming surfaces need the hidden ones, and fetching the two
  // variants separately would cost the worker a second registry query.
  const { data: registry, isLoading, error } = useEntityRegistry(true);

  const withHidden = useMemo<NamingRegistry>(() => {
    const sources = registry?.sources || {};
    const groups = registry?.groups || {};
    const categories = registry?.categories || {};
    const expenseTypes = registry?.expenseTypes || {};
    const payees = registry?.payees || {};
    return {
      sources: sortedValues(sources),
      groups: sortedValues(groups),
      categories: sortedValues(categories),
      expenseTypes: sortedValues(expenseTypes),
      payees: sortedValues(payees),
      getSource: (name) => sources[name],
      getGroup: (name) => groups[name],
      getCategory: (name) => categories[name],
      getExpenseType: (name) => expenseTypes[name],
      getPayee: (name) => payees[name],
    };
  }, [registry]);

  const sourceMap = useMemo(
    () => visibleOnly(registry?.sources || {}),
    [registry],
  );
  const groupMap = useMemo(
    () => visibleOnly(registry?.groups || {}),
    [registry],
  );
  const categoryMap = useMemo(
    () => visibleOnly(registry?.categories || {}),
    [registry],
  );
  const expenseTypeMap = useMemo(
    () => visibleOnly(registry?.expenseTypes || {}),
    [registry],
  );
  const payeeMap = useMemo(
    () => visibleOnly(registry?.payees || {}),
    [registry],
  );
  const invoiceMap = useMemo(() => registry?.invoices || {}, [registry]);

  const sources = useMemo(() => sortedValues(sourceMap), [sourceMap]);
  const groups = useMemo(() => sortedValues(groupMap), [groupMap]);
  const categories = useMemo(() => sortedValues(categoryMap), [categoryMap]);
  const expenseTypes = useMemo(
    () => sortedValues(expenseTypeMap),
    [expenseTypeMap],
  );
  const payees = useMemo(() => sortedValues(payeeMap), [payeeMap]);

  const invoices = useMemo(
    () =>
      Object.values(invoiceMap)
        .filter((x): x is InvoiceEntity => x !== undefined)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [invoiceMap],
  );

  const getSource = (name: string) => sourceMap[name];
  const getGroup = (name: string) => groupMap[name];
  const getCategory = (name: string) => categoryMap[name];
  const getExpenseType = (name: string) => expenseTypeMap[name];
  const getPayee = (name: string) => payeeMap[name];
  const getInvoice = (id: string) => invoiceMap[id as keyof typeof invoiceMap];

  return (
    <RegistryContext.Provider
      value={{
        sources,
        groups,
        categories,
        expenseTypes,
        payees,
        invoices,
        sourceMap,
        groupMap,
        categoryMap,
        expenseTypeMap,
        payeeMap,
        invoiceMap,
        loading: isLoading,
        error,
        getSource,
        getGroup,
        getCategory,
        getExpenseType,
        getPayee,
        getInvoice,
        withHidden,
      }}
    >
      {children}
    </RegistryContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRegistry = () => {
  const context = useContext(RegistryContext);
  if (!context)
    throw new Error("useRegistry must be used within RegistryProvider");
  return context;
};
